import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axiosInstance from './axiosInstance';

function Wordmark() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'center', mb: 3 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'secondary.main' }} />
      <Typography component="span" sx={{ fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.02em', lineHeight: 1, color: 'text.primary' }}>
        Kin<Box component="span" sx={{ color: 'primary.main' }}>flow</Box>
      </Typography>
    </Box>
  );
}

function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length > 0 && confirm.length > 0 && !mismatch && !isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsLoading(true);
    setError('');
    try {
      await axiosInstance.post('/password-reset/confirm/', {
        uid,
        token,
        new_password: password,
      });
      setDone(true);
      // Nach kurzer Bestätigung automatisch zur Anmeldung.
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const data = err.response?.data;
      if (data?.new_password?.length) {
        // Passwortregeln vom Server (Mindestlänge, zu häufig, rein numerisch …)
        setError(data.new_password.join(' '));
      } else if (data?.detail) {
        setError(data.detail);
      } else if (err.response?.status === 429) {
        setError('Zu viele Versuche. Bitte versuche es später erneut.');
      } else {
        setError('Etwas ist schiefgelaufen. Bitte fordere einen neuen Link an.');
      }
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: '0 10px 40px rgba(14,26,43,.08)',
          }}
        >
          <Wordmark />

          {done ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 44, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700 }}>
                Passwort geändert
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1.5, mb: 3 }}>
                Dein Passwort wurde erfolgreich zurückgesetzt. Du wirst zur Anmeldung
                weitergeleitet …
              </Typography>
              <Button component={RouterLink} to="/login" fullWidth variant="contained" size="large">
                Jetzt anmelden
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700 }}>
                Neues Passwort vergeben
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                Wähle ein neues Passwort für dein Kinflow-Konto.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }} noValidate>
                <TextField
                  margin="normal" required fullWidth
                  name="password" label="Neues Passwort" id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password" autoFocus
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal" required fullWidth
                  name="confirm" label="Passwort wiederholen" id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  disabled={isLoading}
                  error={mismatch}
                  helperText={mismatch ? 'Die Passwörter stimmen nicht überein.' : ' '}
                />
                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  sx={{ mt: 1, mb: 2 }} disabled={!canSubmit}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Passwort speichern'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body2" underline="hover">
                    Zurück zur Anmeldung
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default ResetPassword;
